/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { jsx } from '@compiled/react';

import Popup, { type ContentProps, type TriggerProps } from '@atlaskit/popup';

import type { CreateContentProps, UniversalCreateButtonProps } from '../types';
import { findSelectedItem, MenuControlContext, useNormalizeItems } from '../utils';

import MenuContent from './menu-content';
import { TriggerButton } from './trigger-button';
import { useIsResponsiveView } from './utils';

export function UniversalCreateButton({
	children,
	items,
	testId,
	shouldRenderToParent,
	onClick,
	onClose,
	selectedItem,
	product,
	isOpen: controlledOpen = false,
	enableTriggerPulse = false,
	triggerButtonSpotlightTarget,
	isNav4Enabled = true,
	buttonTheme,
}: UniversalCreateButtonProps) {
	const isResponsive = useIsResponsiveView();
	const [isOpen, setIsOpen] = useState(controlledOpen || false);

	const structuredItems = useNormalizeItems(items);

	const defaultMenu = useCallback(() => <MenuContent />, []);
	const [createContent, setCreateContent] = useState<(contentProps: ContentProps) => ReactNode>(
		() => defaultMenu,
	);

	const reset = useCallback(() => {
		setCreateContent(() => defaultMenu);
	}, [defaultMenu]);

	const close = useCallback(() => {
		setCreateContent(() => defaultMenu);
		setIsOpen(false);
	}, [defaultMenu]);

	const load = useCallback(
		(loadCreateContent: CreateContentProps) => {
			setCreateContent(
				() => (contentProps: ContentProps) =>
					loadCreateContent(contentProps, {
						reset,
						close,
					}),
			);
		},
		[close, reset],
	);

	const triggerButtonClick = useCallback(() => {
		setIsOpen((prev) => {
			if (prev) {
				onClose?.();
			}
			return !prev;
		});
		onClick?.();
	}, [onClick, onClose, setIsOpen]);

	const popupTrigger = useCallback(
		(triggerProps: TriggerProps) => (
			<TriggerButton
				testId={testId}
				triggerButtonSpotlightTarget={triggerButtonSpotlightTarget}
				enableTriggerPulse={enableTriggerPulse}
				isOpen={isOpen}
				onClick={triggerButtonClick}
				triggerProps={triggerProps}
				product={product}
				isNav4Enabled={isNav4Enabled}
				buttonTheme={buttonTheme}
			>
				{children}
			</TriggerButton>
		),
		[
			children,
			enableTriggerPulse,
			isOpen,
			product,
			testId,
			triggerButtonClick,
			triggerButtonSpotlightTarget,
			buttonTheme,
			isNav4Enabled,
		],
	);

	const onCloseMenu = useCallback(() => {
		setIsOpen(false);
		setCreateContent(() => defaultMenu);
		onClose?.();
	}, [defaultMenu, onClose]);

	const foundItem = useMemo(
		() => selectedItem && findSelectedItem(structuredItems, selectedItem),
		[selectedItem, structuredItems],
	);

	useEffect(() => {
		if (isOpen) {
			const loadContent = foundItem?.loadCreateContent;
			if (loadContent) {
				setCreateContent(
					() => (contentProps: ContentProps) =>
						loadContent(contentProps, {
							reset,
							close,
						}),
				);
			} else {
				reset();
			}
		}
	}, [close, isOpen, foundItem?.loadCreateContent, reset]);

	useEffect(() => {
		if (controlledOpen !== undefined) {
			setIsOpen(controlledOpen);
		}
	}, [controlledOpen]);

	return (
		<MenuControlContext.Provider
			value={{
				reset,
				close,
				load,
				mixedItems: structuredItems,
			}}
		>
			<Popup
				isOpen={isOpen}
				placement={isResponsive && isNav4Enabled ? 'bottom-end' : 'bottom-start'}
				shouldRenderToParent={shouldRenderToParent}
				onClose={onCloseMenu}
				content={createContent}
				trigger={popupTrigger}
			/>
		</MenuControlContext.Provider>
	);
}

export default UniversalCreateButton;
